import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import HexGrid from '../HexGrid';
import BotRunner from '../BotRunner/BotRunner';
import ReplayRunner from '../ReplayRunner/ReplayRunner';
import MenuDemoTurnBadge from './MenuDemoTurnBadge/MenuDemoTurnBadge';
import MenuDemoLoader from './MenuDemoLoader/MenuDemoLoader';
import MenuDemoPolicyToast from './MenuDemoPolicyToast/MenuDemoPolicyToast';
import MenuDemoSetupIntro from './MenuDemoSetupIntro/MenuDemoSetupIntro';
import GameEndPlayerInfo from '../GameEndContent/GameEndPlayerInfo/GameEndPlayerInfo';
import {ReplayControlsProvider} from '../../contexts/ReplayControlsContext';
import {
  hideToast,
  loadState,
  resetModalVisibility,
  setBotsPlannerMode,
  setGameType,
  setPlayers,
  setPolicyMarket,
  startGame
} from '../../redux/actions';
import {GAME_TYPES} from '../../constants/game';
import {resetBotEffectBuffers} from '../../utils/bots/botEffects';
import {GAME_FIELDS} from '../../configs/fields';
import {FACTION_CONFIGS, FACTION_IDS_IMPLEMENTED} from '../../configs/factions';
import {filterBotFactionPool} from '../../configs/botExcludedFactions';
import {getRandomElement} from '../../utils/other/getRandomElement';
import {getNewPolicyMarket} from '../../utils/policy/getNewPolicyMarket';
import {applyFactionColorsToLocalGame} from '../../utils/players/applyFactionColorsToLocalGame';
import {getPlayerById} from '../../utils/players/getPlayerById';
import {getRandomInitiativeShift} from '../../utils/gameState/getRandomInitiativeShift';
import {getRandomGroundBackground} from '../../configs/groundBackgrounds';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {useUnblockedOnce} from '../../hooks/react/useUnblockedOnce';
import {useImagesPreloaded} from '../../hooks/react/useImagesPreloaded';
import {getSetupPlayersModalImagePaths} from '../Modals/SetupPlayersModal/getSetupPlayersModalImagePaths';
import {resetBotStepGuards} from '../../utils/bots/executeBotStep';
import {
  BOT_ACTION_INTERVAL_MS,
  DEMO_BOT_GAME_SPEED_LOCAL_STORAGE_KEY
} from '../../utils/bots/botConfig';
import {
  getDemoAutoVolume,
  readDemoStartVolume,
  saveChosenDemoVolume,
  saveDemoVolume
} from '../../utils/sound/demoVolumeStorage';
import {COINS_TO_WIN_POINTS_CONVERSION, FINAL_ROUND} from '../../configs/game';
import {ENLIGHTENED_COINS_TO_WIN_POINTS_CONVERSION} from '../../configs/factions/enlightened';
import {isPlayerFaction} from '../../utils/factions/isPlayerFaction';
import {FACTIONS} from '../../constants/factions';
import {
  selectGameResults,
  selectPhase,
  selectPlayers,
  selectRound,
  selectTurnOrder
} from '../../redux/reducers/sharedStateReducer/selectors';
import {PHASES} from '../../constants/phases';
import {
  MENU_REPLAY_ACTION_INTERVAL_MS,
  MENU_SHOWCASE_MODE,
  MENU_SHOWCASE_MODES,
  MENU_SHOWCASE_SPEED_DEFAULT,
  MENU_SHOWCASE_SPEED_MAX
} from '../../configs/menuShowcase';
import {
  dispatchReplayBootstrapActions,
  splitReplayBootstrapActions
} from '../../utils/replay/splitReplayBootstrapActions';
import {findPolicyPurchase} from '../../utils/policy/findPolicyPurchase';
import {API_BASE} from '../../utils/apiConfig';
import {getGameResultsPlaces} from '../../utils/gameEnd/compareGameResults';
import {getMenuShowcaseLayoutKey, getMenuShowcaseSize} from '../../utils/menu/getMenuShowcaseSize';
import {sendButtonClickEvent, sendPageEvent} from '../../analytics/gameAnalytics';

const noop = () => {
};

// Длительность fade-out поля перед перемонтированием — совпадает с transition
// opacity у .game-container.menu-demo-game / .menu-demo-match-end-banner
const RESTART_FADE_OUT_DURATION_MS = 1200;
// Сколько демо-партия остается на экране (с плашкой победителя) после конца
const RESTART_DELAY_MS = 20000;
// Запись кончилась, а итогов партии в ней нет (матч бросили) — показывать
// нечего, молча берем следующую
const FINISHED_WITHOUT_RESULTS_DELAY_MS = 4000;

// Восстановление множителя скорости из localStorage: только целые значения,
// прижатые к актуальным лимитам 1..MENU_SHOWCASE_SPEED_MAX;
// все прочее (мусор, недоступный localStorage, отсутствие записи) — скорость
// по умолчанию MENU_SHOWCASE_SPEED_DEFAULT
const readInitialDemoSpeed = () => {
  try {
    const stored = localStorage.getItem(DEMO_BOT_GAME_SPEED_LOCAL_STORAGE_KEY);
    if (stored === null) return MENU_SHOWCASE_SPEED_DEFAULT;
    const parsed = Number(stored);
    if (!Number.isInteger(parsed)) return MENU_SHOWCASE_SPEED_DEFAULT;
    return Math.min(MENU_SHOWCASE_SPEED_MAX, Math.max(1, parsed));
  } catch {
    return MENU_SHOWCASE_SPEED_DEFAULT;
  }
};

// Текущие (ещё не финальные) очки живого игрока — та же формула, что и
// buildGameResults для finalWinPoints по концу игры (winPoints + монеты по
// курсу, у просвещённых курс льготный), только на живых, а не финальных
// данных — нужно, чтобы на плашке по ходу партии отображалось то же число,
// в которое оно превратится по завершении, без скачка в момент конца игры
// Как подписан игрок в плашке витрины. В записи реального матча — настоящий
// никнейм, а фракция уточнением через тире той же строкой и тем же цветом;
// в партии ботов имена вида «Бот 3» смысла не несут, поэтому там показывается
// сама фракция
const getShowcasePlayerName = (player, factionName, isReplayMode) => {
  if (!isReplayMode) {
    return factionName || player.name;
  }
  return factionName ? `${player.name} (${factionName})` : player.name;
};

const getLiveFinalWinPoints = (player) => {
  const conversionRate = isPlayerFaction(player, FACTIONS.ENLIGHTENED)
    ? ENLIGHTENED_COINS_TO_WIN_POINTS_CONVERSION
    : COINS_TO_WIN_POINTS_CONVERSION;
  return player.winPoints + Math.floor(player.coins / conversionRate);
};

/**
 * Витрина на главном меню (десктоп). Режим выбирается одной константой
 * MENU_SHOWCASE_MODE (см. configs/menuShowcase.js):
 *
 *  - REPLAY (по умолчанию) — запись случайного реального матча с сервера:
 *    настоящие никнеймы и настоящие решения игроков, шаг воспроизведения
 *    MENU_REPLAY_ACTION_INTERVAL_MS;
 *  - BOT_DEMO — живая партия ботов прямо в браузере (прежнее поведение):
 *    случайное поле, случайные фракции, все игроки — боты.
 *
 * На BOT_DEMO витрина откатывается сама, если запись получить не удалось
 * (нет сети, пустая база записей) — меню не должно оставаться с пустой
 * половиной экрана.
 *
 * Прежнее описание демо-партии ботов, актуальное для обоих режимов: Рендерится только игровое поле (HexGrid) без
 * интерфейса взаимодействия; игровые звуки по умолчанию выключены (слайдер
 * громкости позволяет включить, см. demoVolume); поле некликабельно
 * (кроме пана/зума камеры). По завершении партии кнопка фазы скрывается,
 * над полем проявляется плашка с победителем, а через RESTART_DELAY_MS поле
 * плавно скрывается и родитель (MainMenu) перемонтирует компонент — стартует
 * новая демо-партия.
 */
const MenuDemoGame = ({onRestartRequested, isBlockedByModals}) => {
  const dispatch = useDispatch();
  // Фактический режим витрины: стартует с настроенного и может откатиться на
  // партию ботов, если запись не получена
  const [showcaseMode, setShowcaseMode] = useState(MENU_SHOWCASE_MODE);
  const isReplayMode = showcaseMode === MENU_SHOWCASE_MODES.REPLAY;
  // Дата матча из записи — ею подписан лоадер («Запускаем реплей от 15 марта»)
  // const [replayDateLabel, setReplayDateLabel] = useState('');
  // Запись доиграна до конца (см. onFinished у ReplayRunner)
  const [isReplayFinished, setIsReplayFinished] = useState(false);
  const replayBootstrapActionsRef = useRef(null);
  const replayGameplayActionsRef = useRef(null);
  // Размер витрины на узких экранах (на десктопе — null, там своя область
  // справа от панели меню). Пересчитывается на ресайзе вместе с пересборкой
  // поля: сцена Phaser создается под конкретный размер
  const [showcaseSize, setShowcaseSize] = useState(getMenuShowcaseSize);
  const isCompactShowcase = Boolean(showcaseSize);
  const rootRef = useRef(null);
  const bannerRef = useRef(null);
  const [isStateReady, setIsStateReady] = useState(false);
  // Поле остается прозрачным, пока ресурсы игры не загружены и сцена не
  // отрендерена (тот же сигнал готовности, что у экрана загрузки GameContent),
  // затем плавно проявляется через CSS-переход
  const [isGameReady, setIsGameReady] = useState(false);
  // Партия стартует не сразу после загрузки записи, а после представления
  // игроков (MenuDemoSetupIntro) — той же плашки «Жребий брошен!», что и в
  // начале настоящего матча. Пока флаг не поднят, ReplayRunner/BotRunner не
  // смонтированы, и запись стоит на первом кадре
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  // Пока над меню висит хоть одна модалка, витрина не начинается: ни
  // представления игроков, ни первого действия записи — человек их попросту не
  // видит. Именно защелка, а не пауза: открытая позже модалка (магазин,
  // правила) уже идущую запись не останавливает — размонтирование
  // ReplayRunner отмотало бы ее к началу
  const isStartUnblocked = useUnblockedOnce(isBlockedByModals);
  // При изменении размера окна HexGrid размонтируется и монтируется заново
  // (как в GameContent), чтобы Phaser пересчитал размеры сцены от актуального
  // window.innerWidth/innerHeight; на время пересборки поле прячется через
  // isGameReady и снова проявляется по готовности новой сцены
  const [hideGameForResize, setHideGameForResize] = useState(false);
  // Лоадер крутится, пока витрина не готова (поле и картинки представления,
  // см. isShowcaseReady); по готовности плавно затухает (hiding) и после
  // CSS-перехода размонтируется (hidden). При пересборке поля на ресайзе
  // возвращается, пока новая сцена не будет готова
  const [loaderState, setLoaderState] = useState('spinning');
  // Плашка "Матч завершён" проявляется с небольшой задержкой после конца
  // партии, чтобы сработал CSS-переход opacity, а не мгновенное появление
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  // Через RESTART_DELAY_MS после конца партии поле (и плашка) плавно
  // скрываются по opacity, затем запрашивается перемонтирование у родителя
  const [isFadingOutForRestart, setIsFadingOutForRestart] = useState(false);
  // Целочисленный множитель скорости демо-партии (×1..×MAX): интервал между
  // действиями ботов делится на него; значение сохраняется в localStorage
  const [demoSpeed, setDemoSpeed] = useState(readInitialDemoSpeed);
  // Громкость звука демо-партии 0..1 (по умолчанию 0 — см.
  // readInitialDemoVolume); значение сохраняется в localStorage под своим
  // ключом и читается SoundManager напрямую оттуда (см. _getGlobalVolume) —
  // отдельного проброса/синхронизации в Phaser не требуется
  const [demoVolume, setDemoVolume] = useState(readDemoStartVolume);
  // Плашка раунда/результатов по умолчанию свернута в любой раскладке:
  // развернутый список игроков закрывает заметную часть поля, а витрина в
  // первую очередь про само поле. Разворачивается по клику пользователя и
  // автоматически — когда партия завершается (см. эффект ниже)
  const [isBannerCollapsed, setIsBannerCollapsed] = useState(true);
  // Обратный отсчёт "Новый матч через X сек" внизу плашки результатов
  const [restartCountdownSec, setRestartCountdownSec] = useState(null);

  // Гость (профиля еще нет) или вошедший игрок — уходит уровнем в метрику
  // старта витрины: интересно, кому именно мы ее показываем
  const isGuest = useSelector((state) => Boolean(state.private.auth.isGuest));
  const gameResults = useSelector(selectGameResults);
  const players = useSelector(selectPlayers);
  const round = useSelector(selectRound);
  const phase = useSelector(selectPhase);
  const turnOrder = useSelector(selectTurnOrder);
  // Покупка политики — самое заметное решение партии, но на поле она никак не
  // видна: показываем тостом. Ключ нужен, чтобы каждая следующая покупка
  // пересоздавала тост (и его анимацию), даже если предыдущий еще на экране
  const [policyPurchase, setPolicyPurchase] = useState(null);
  const prevPlayersRef = useRef(null);
  // Тост живет ровно под свернутой плашкой; развернутая занимает его место,
  // поэтому при ней покупки не показываем вовсе. Состояние плашки читаем
  // через ref: эффект ниже должен реагировать на игроков, а не перезапускаться
  // от сворачивания
  const isBannerCollapsedRef = useRef(true);
  useEffect(() => {
    const purchase = findPolicyPurchase(prevPlayersRef.current, players);
    prevPlayersRef.current = players;
    if (!purchase || !isBannerCollapsedRef.current) return;
    setPolicyPurchase({...purchase, key: `${purchase.playerId}:${purchase.policy.id}:${Date.now()}`});
  }, [players]);
  // isStateReady гейтит от гонки при перемонтировании (новая демо-партия
  // после рестарта): между размонтированием старого экземпляра и тем, как
  // dispatch(loadState(...)) для нового реально отработает (после async
  // fetch поля), Redux ещё хранит gameResults ПРЕДЫДУЩЕЙ партии — без этого
  // гейта плашка на миг принудительно показывалась бы поверх лоадера
  const hasMatchEnded = isStateReady && Boolean(gameResults && gameResults.length > 0);

  // Плашка представления рисует состав по очередности хода и только в фазе
  // SETUP (см. SetupPlayersModal). Если запись начинается не с нее или состав
  // не пришел — представлять нечего, и партию задерживать не за чем
  const hasSetupIntro = phase === PHASES.SETUP
    && players.length > 0
    && Boolean(turnOrder && turnOrder.length > 0);

  // Картинки плашки представления — по скинам, которые выбрали игроки записи,
  // и заранее браузер их обычно не видел. Анимация начинается сразу за
  // лоадером, и без предзагрузки строки вылетали бы с пустыми иконками, а
  // большие картинки фракций проявлялись бы по кускам. Грузим их параллельно
  // с полем и держим лоадер, пока не готовы и они. После представления список
  // пуст: идущую партию ожидание уже не касается (в том числе пересборку поля
  // на ресайзе)
  const setupIntroImagePaths = useMemo(() => {
    if (!isStateReady || !hasSetupIntro || isIntroComplete) {
      return [];
    }
    const playersById = new Map(players.map((player) => [player.id, player]));
    const introPlayers = turnOrder.map((playerId) => playersById.get(playerId)).filter(Boolean);
    return getSetupPlayersModalImagePaths(introPlayers);
  }, [isStateReady, hasSetupIntro, isIntroComplete, players, turnOrder]);
  const areSetupIntroImagesReady = useImagesPreloaded(setupIntroImagePaths);
  // Витрину показываем (уходит лоадер, проявляются поле и представление),
  // когда готово и поле, и картинки представления
  const isShowcaseReady = isGameReady && areSetupIntroImagesReady;

  useEffect(() => {
    let isCancelled = false;

    // Запись реального матча: служебный префикс применяем сразу (иначе полю
    // нечего рисовать), остальное отдаем ReplayRunner — он проигрывает по
    // действию раз в MENU_REPLAY_ACTION_INTERVAL_MS
    const startReplay = () => fetch(`${API_BASE}/matches/random-replay`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Random replay request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (isCancelled) {
          return;
        }
        const {bootstrapActions, gameplayActions} = splitReplayBootstrapActions(data.actions);
        if (gameplayActions.length === 0) {
          throw new Error('Random replay has no gameplay actions');
        }

        dispatch(setGameType(GAME_TYPES.REPLAY));
        dispatch(setBotsPlannerMode(false));
        dispatchReplayBootstrapActions(dispatch, bootstrapActions);

        replayBootstrapActionsRef.current = bootstrapActions;
        replayGameplayActionsRef.current = gameplayActions;
        // setReplayDateLabel(formatDayMonth(data.finishedAt));
        setIsStateReady(true);
      })
      .catch((error) => {
        // Записей нет вовсе, сеть недоступна, запись битая — витрина не
        // должна оставаться пустой, показываем партию ботов
        console.error('[MenuDemoGame] replay init failed, falling back to bot demo', error);
        if (!isCancelled) {
          setShowcaseMode(MENU_SHOWCASE_MODES.BOT_DEMO);
        }
      });

    const startBotDemo = () => {
      const field = getRandomElement(GAME_FIELDS);
      return fetch(field.path)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Demo field load failed: ${field.path}`);
        }
        return response.json();
      })
      .then((fieldData) => {
        if (isCancelled) {
          return;
        }
        const demoPlayers = (fieldData.playerColors || []).map((color, index) => ({
          color,
          name: `Бот ${index + 1}`,
          isBot: true,
          // Те же фракции, что запрещены ботам в рейтинге, не берем и в демо:
          // поведение ботов под них не настроено, и в витрине меню они играют
          // заметно слабее
          factionId: getRandomElement(filterBotFactionPool(FACTION_IDS_IMPLEMENTED)),
        }));
        const {coloredPlayers, coloredFieldData} = applyFactionColorsToLocalGame(demoPlayers, fieldData);
        // Новая партия с теми же id игроков (id из цветов поля) — счетчики
        // движка ботов прошлого демо не должны переживать перезапуск
        resetBotStepGuards();
        dispatch(setGameType(GAME_TYPES.LOCAL));
        // Планировщик ботов (волна 29) в демо всегда выключен: LOAD_STATE
        // сохраняет флаг прошлой партии, а админ мог играть локалку с ним
        dispatch(setBotsPlannerMode(false));
        dispatch(loadState(coloredFieldData, 13));
        dispatch(setPlayers(coloredPlayers));
        dispatch(setPolicyMarket(getNewPolicyMarket()));
        // Витрина в режиме партии ботов — та же локальная игра, подложку
        // выбирает сама. В режиме реплея подложка приходит в самой записи
        dispatch(startGame(
          Date.now(), true, false, getRandomInitiativeShift(coloredPlayers.length),
          false, getRandomGroundBackground()
        ));
        setIsStateReady(true);
      })
      .catch((error) => {
        console.error('[MenuDemoGame] init failed', error);
      });
    };

    if (isReplayMode) {
      startReplay();
    } else {
      startBotDemo();
    }

    return () => {
      isCancelled = true;
    };
  }, [dispatch, isReplayMode]);

  // Демо-партия крутится на том же сторе, что и настоящая игра: конец ее
  // раунда/матча дергает modalsMiddleware так же, как в настоящей партии,
  // выставляя showGameEndModal и т.п. в
  // private-стейте. Эти флаги не сбрасываются сами при уходе с демо (LOAD_STATE
  // настоящей партии их не трогает), поэтому чистим при размонтировании —
  // до того, как пользователь попадет в реальную игру.
  //
  // Тот же мусор оставляют эффекты записи: тост витрины («крысолюды украли
  // монету» и прочие фракционные) в меню никто не видит и не снимает — Toast
  // смонтирован только на экране матча, — и он всплывал в начале настоящей
  // партии. Отложенные эффекты (botEffects) тоже забываем: иначе они отыграют
  // со звуком уже после ухода из меню
  useEffect(() => {
    return () => {
      dispatch(resetModalVisibility());
      dispatch(hideToast());
      resetBotEffectBuffers();
    };
  }, [dispatch]);

  // Громкость витрины хранится под своим ключом, не пересекаясь с громкостью
  // обычных игр: SoundManager читает ее из localStorage сам, поэтому в Phaser
  // ничего пробрасывать не нужно. Стартовое значение записываем сразу —
  // сцена смотрит именно в хранилище, решая, грузить ли звуки вообще
  useEffect(() => {
    saveDemoVolume(demoVolume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let debounceId;
    let hideId;
    // Пересобираем сцену только когда меняется сама раскладка витрины. На
    // телефоне resize прилетает на каждом скролле меню (браузер прячет
    // адресную строку и меняет высоту окна), а витрина там зависит только от
    // ширины — иначе каждый скролл ронял поле в лоадер
    let layoutKey = getMenuShowcaseLayoutKey();
    const handleResize = () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        const nextLayoutKey = getMenuShowcaseLayoutKey();
        if (nextLayoutKey === layoutKey) {
          return;
        }
        layoutKey = nextLayoutKey;
        setIsGameReady(false);
        setShowcaseSize(getMenuShowcaseSize());
        setHideGameForResize(true);
        clearTimeout(hideId);
        hideId = setTimeout(() => {
          setHideGameForResize(false);
        }, 50);
      }, 300);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(debounceId);
      clearTimeout(hideId);
    };
  }, []);

  // Витрина доехала до первого кадра — считаем это стартом показа. Событие
  // одно на партию: компонент перемонтируется под каждый следующий матч
  const isShowcaseStartReportedRef = useRef(false);
  useEffect(() => {
    if (!isShowcaseReady || isShowcaseStartReportedRef.current) return;
    isShowcaseStartReportedRef.current = true;
    sendPageEvent('MenuShowcase', 'start', showcaseMode, isGuest ? 'guest' : 'player');
  }, [isShowcaseReady, showcaseMode, isGuest]);

  useEffect(() => {
    if (!isShowcaseReady) {
      setLoaderState('spinning');
      return;
    }
    setLoaderState('hiding');
    // Длительность совпадает с transition в MenuDemoLoader.css
    const hideTimer = setTimeout(() => setLoaderState('hidden'), 600);
    return () => clearTimeout(hideTimer);
  }, [isShowcaseReady]);

  useEffect(() => {
    if (!hasMatchEnded) {
      setIsBannerVisible(false);
      return;
    }
    // Небольшая задержка, чтобы элемент успел смонтироваться с opacity: 0
    // до применения класса видимости — иначе CSS-переход не сработает
    const showTimer = setTimeout(() => setIsBannerVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, [hasMatchEnded]);

  // По завершении партии плашка результатов всегда разворачивается сама,
  // даже если пользователь свернул её во время игры
  useEffect(() => {
    if (hasMatchEnded) {
      setIsBannerCollapsed(false);
    }
  }, [hasMatchEnded]);

  useEffect(() => {
    if (!hasMatchEnded) return;
    const restartDelayTimer = setTimeout(() => {
      setIsFadingOutForRestart(true);
    }, RESTART_DELAY_MS);
    return () => clearTimeout(restartDelayTimer);
  }, [hasMatchEnded]);

  // Запись доиграна, а итогов партии в ней нет: матч бросили на середине или
  // сервер не дописал концовку. Показывать нечего — быстро уходим за
  // следующей записью, не выдерживая полную паузу с плашкой результатов
  useEffect(() => {
    if (!isReplayFinished || hasMatchEnded) return;
    const nextReplayTimer = setTimeout(() => {
      setIsFadingOutForRestart(true);
    }, FINISHED_WITHOUT_RESULTS_DELAY_MS);
    return () => clearTimeout(nextReplayTimer);
  }, [isReplayFinished, hasMatchEnded]);

  // Считает оставшееся время не декрементом на тик, а от фактического
  // времени окончания — чтобы не дрейфовать при уходе вкладки в фон
  useEffect(() => {
    if (!hasMatchEnded) {
      setRestartCountdownSec(null);
      return;
    }
    const restartAt = Date.now() + RESTART_DELAY_MS;
    const tick = () => {
      setRestartCountdownSec(Math.max(0, Math.ceil((restartAt - Date.now()) / 1000)));
    };
    tick();
    const intervalId = setInterval(tick, 250);
    return () => clearInterval(intervalId);
  }, [hasMatchEnded]);

  useEffect(() => {
    if (!isFadingOutForRestart) return;
    const restartTimer = setTimeout(() => {
      onRestartRequested?.();
    }, RESTART_FADE_OUT_DURATION_MS);
    return () => clearTimeout(restartTimer);
  }, [isFadingOutForRestart, onRestartRequested]);

  useEffect(() => {
    try {
      localStorage.setItem(DEMO_BOT_GAME_SPEED_LOCAL_STORAGE_KEY, String(demoSpeed));
    } catch {
      // localStorage недоступен (приватный режим и т.п.) — просто не сохраняем
    }
  }, [demoSpeed]);

  const handleReplayFinished = useCallback(() => setIsReplayFinished(true), []);
  const handleLoadingProgress = useCallback(noop, []);
  const handleGameReady = useCallback(() => setIsGameReady(true), []);
  const handleIntroFinished = useCallback(() => setIsIntroComplete(true), []);

  useEffect(() => {
    if (isGameReady && !hasSetupIntro) {
      setIsIntroComplete(true);
    }
  }, [isGameReady, hasSetupIntro]);

  const isSetupIntroVisible = isStateReady
    && isShowcaseReady
    && isStartUnblocked
    && hasSetupIntro
    && !isIntroComplete;
  const handleDemoSpeedChange = useCallback((event) => {
    setDemoSpeed(Number(event.target.value));
  }, []);
  const handleDemoVolumeChange = useCallback((event) => {
    const nextVolume = parseFloat(event.target.value);
    // Ползунок двинул человек — дальше витрина в его громкость не вмешивается
    saveChosenDemoVolume(nextVolume);
    if (nextVolume > 0) {
      // Громкость подняли выше 0 уже после того, как сцена загрузилась и
      // preload() пропустил загрузку звуков — догружаем их сейчас; вызов
      // идемпотентен, если звуки уже загружены/грузятся — просто no-op
      REGISTRY_SINGLETON.getSoundManager().ensureSoundsLoaded();
    }
    setDemoVolume(nextVolume);
  }, []);
  // Кнопка звука компактной витрины: слайдера там нет (он desktop-удобство),
  // а управлять звуком нужно. Переключает между тишиной и той же тихой
  // громкостью, которую витрина ставит себе сама после загрузки поля.
  // Нажатие считается выбором игрока — дальше автоподъем в его громкость не
  // вмешивается (см. demoVolumeStorage)
  const handleToggleShowcaseSound = useCallback(() => {
    const nextVolume = demoVolume > 0 ? 0 : getDemoAutoVolume();
    saveChosenDemoVolume(nextVolume);
    if (nextVolume > 0) {
      // Звуки могли не грузиться вовсе — витрина стартует беззвучной
      REGISTRY_SINGLETON.getSoundManager().ensureSoundsLoaded();
    }
    sendButtonClickEvent('MenuShowcase', 'sound_toggle', nextVolume > 0 ? 'on' : 'off');
    setDemoVolume(nextVolume);
  }, [demoVolume]);

  const handleToggleBannerCollapsed = useCallback(() => {
    setIsBannerCollapsed((prev) => !prev);
  }, []);

  // Подпись под лоадером: у записи — дата матча, если сервер ее прислал
  const loaderText = isReplayMode
    // ? (replayDateLabel ? `Запускаем реплей от ${replayDateLabel}` : 'Запускаем случайный реплей...')
    ? 'Запускаю случайный реплей...'
    : 'Запускаю демо...';

  const isFieldVisible = isShowcaseReady && !isFadingOutForRestart;
  // Плашка принудительно на полной непрозрачности (вне зависимости от hover),
  // когда партия завершена; пока идёт партия — прячется/показывается только
  // по hover на .menu-demo-root (см. CSS)
  const isBannerEndedVisible = hasMatchEnded && isBannerVisible && !isFadingOutForRestart;

  useEffect(() => {
    isBannerCollapsedRef.current = isBannerCollapsed;
    // Плашку развернули (в том числе автоматически по концу партии) — тост
    // убираем сразу, иначе он остался бы висеть под ее заголовком
    if (!isBannerCollapsed) {
      setPolicyPurchase(null);
    }
  }, [isBannerCollapsed]);

  // Тост выезжает справа: на десктопе — поверх плашки, верхними краями
  // вровень, в компактной раскладке — под ней. Размеры плашки зависят от
  // раскладки (на телефоне шрифт и отступы мельче) и от состояния, поэтому
  // не хардкодим их в CSS, а измеряем и отдаем переменными на корне витрины
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    const root = rootRef.current;
    if (!banner || !root) return;

    const updateBannerBounds = () => {
      // В компактной раскладке и плашка, и тост позиционированы внутри
      // витрины, на десктопе — оба fixed от края экрана
      const rect = banner.getBoundingClientRect();
      const top = isCompactShowcase ? banner.offsetTop : rect.top;
      const bottom = isCompactShowcase ? banner.offsetTop + banner.offsetHeight : rect.bottom;
      root.style.setProperty('--menu-demo-banner-top', `${Math.round(top)}px`);
      root.style.setProperty('--menu-demo-banner-bottom', `${Math.round(bottom)}px`);
    };

    updateBannerBounds();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateBannerBounds);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [isCompactShowcase, isBannerCollapsed, hasMatchEnded]);

  return (
    // Провайдер управления просмотром — на корне витрины: он нужен и
    // ReplayRunner (пауза/скорость), и кнопке фазы ниже. Двух отдельных
    // провайдеров быть не должно — это два разных состояния
    <ReplayControlsProvider>
    <div
      ref={rootRef}
      className={`menu-demo-root${isCompactShowcase ? ' menu-demo-root_compact' : ''}`}
      style={showcaseSize ? {width: showcaseSize.width, height: showcaseSize.height} : undefined}
    >
      <div className={`game-container menu-demo-game${isFieldVisible ? ' menu-demo-game_visible' : ''}`}>
        {isStateReady && !hideGameForResize &&
          <HexGrid
            onLoadingProgress={handleLoadingProgress}
            onGameReady={handleGameReady}
            isDemoMode={true}
            sceneSize={showcaseSize}
          />
        }
        {isStateReady && isIntroComplete && isStartUnblocked && (isReplayMode ? (
          <ReplayRunner
            actions={replayGameplayActionsRef.current}
            bootstrapActions={replayBootstrapActionsRef.current}
            actionIntervalMs={Math.round(MENU_REPLAY_ACTION_INTERVAL_MS / demoSpeed)}
            // Число на ползунке скорости: до x5 сцена показывает анимации поля
            // (переход отрядов, залпы, удары), как на странице записи
            playbackSpeedMultiplier={demoSpeed}
            onFinished={handleReplayFinished}
          />
        ) : (
          <BotRunner actionIntervalMs={Math.round(BOT_ACTION_INTERVAL_MS / demoSpeed)}/>
        ))}
      </div>
      {/* Отдельно от .menu-demo-game: тот контейнер прозрачен до готовности
          поля и скрыл бы лоадер; геометрия области повторена в CSS лоадера */}
      {loaderState !== 'hidden' && (
        <MenuDemoLoader
          isHiding={loaderState === 'hiding'}
          text={loaderText}
          isCompact={isCompactShowcase}
        />
      )}
      {/* Представление игроков: та же плашка, что в начале настоящего матча,
          но внутри области витрины. Показывается, когда готовы поле и
          картинки плашки (лоадер уже уходит), и держит партию до конца
          анимации */}
      {isSetupIntroVisible && (
        <MenuDemoSetupIntro
          playerCount={players.length}
          showcaseSize={showcaseSize}
          isReplay={isReplayMode}
          soundVolume={demoVolume}
          onFinished={handleIntroFinished}
        />
      )}
      {/* Слайдер скорости демо-трансляции: левый нижний угол области поля,
          стиль — как у слайдера громкости в настройках. Проявляется по
          opacity только после загрузки поля (когда скрыт лоадер) и плавно
          затухает при пересборке поля/рестарте */}
      <div
        className={`menu-demo-speed-slider volume-slider-container${isFieldVisible ? ' menu-demo-speed-slider_visible' : ''}`}
      >
        <img
          className="volume-slider-icon menu-demo-speed-slider-icon"
          src="/img/icons/upgrade.png"
          alt={isReplayMode ? 'Скорость реплея' : 'Скорость демо-игры'}
          title={isReplayMode ? 'Скорость реплея' : 'Скорость демо-игры'}
        />
        <input
          type="range"
          min="1"
          max={MENU_SHOWCASE_SPEED_MAX}
          step="1"
          value={demoSpeed}
          onChange={handleDemoSpeedChange}
          className="volume-slider"
        />
        <span className="volume-slider-value">×{demoSpeed}</span>
      </div>
      {/* Слайдер громкости демо-трансляции: сразу под слайдером скорости,
          тот же визуальный стиль (как и у слайдера громкости в настройках).
          По умолчанию 0% — см. readInitialDemoVolume/SoundManager.preload */}
      <div
        className={`menu-demo-volume-slider volume-slider-container${isFieldVisible ? ' menu-demo-volume-slider_visible' : ''}`}
      >
        <img
          className="volume-slider-icon"
          src={demoVolume === 0 ? '/img/icons/volume_off.png' : '/img/icons/volume.png'}
          alt={isReplayMode ? 'Звук реплея' : 'Звук демо-игры'}
          title={isReplayMode ? 'Звук реплея' : 'Звук демо-игры'}
        />
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={demoVolume}
          onChange={handleDemoVolumeChange}
          className="volume-slider"
        />
        <span className="volume-slider-value">{Math.round(demoVolume * 100)}%</span>
      </div>
      {/* Отдельно от .menu-demo-game: тот контейнер намеренно смещен
          (transform для маски по краю) и создает свой containing block —
          вложенный absolute/fixed считал бы отступы от смещенного контейнера,
          а не от реального края экрана. Скрывается насовсем по завершении
          партии — победителя показывает плашка ниже */}
      {isStateReady && !hasMatchEnded && (
        <div className={`menu-demo-phase-button${isShowcaseReady ? ' menu-demo-phase-button_visible' : ''}`}>
          {/* Чей ход — простая плитка, а не кнопка хода матча: та на
              дизайн-системе матча (кольцо таймера, часы, счетчик дел) */}
          <MenuDemoTurnBadge/>
        </div>
      )}
      {policyPurchase && isBannerCollapsed && (
        <MenuDemoPolicyToast
          key={policyPurchase.key}
          player={getPlayerById(players, policyPurchase.playerId)}
          policy={policyPurchase.policy}
          isCompact={isCompactShowcase}
          onHidden={() => setPolicyPurchase(null)}
        />
      )}

      {/* Плашка рендерится всегда (не только по завершении партии): по ходу
          игры показывает текущий раунд и проявляется только по hover на
          .menu-demo-root (см. CSS), а по завершении партии — переключается
          на результаты и остаётся на полной непрозрачности всегда */}
      <div
        ref={bannerRef}
        className={`menu-demo-match-end-banner${isFieldVisible ? ' menu-demo-match-end-banner_visible' : ''}${isBannerEndedVisible ? ' menu-demo-match-end-banner_ended' : ''}${isBannerCollapsed ? ' menu-demo-match-end-banner_collapsed' : ''}`}
      >
        {/* Кнопка звука стоит слева от плашки: в компактной раскладке
            слайдеры скрыты, и это единственный способ включить звук */}
        {isCompactShowcase && (
          <button
            type="button"
            className="menu-demo-sound-toggle"
            onClick={handleToggleShowcaseSound}
            title={demoVolume > 0 ? 'Выключить звук' : 'Включить звук'}
          >
            {/* Маска, а не <img>: так иконка красится в --text-primary-color */}
            <span
              className="menu-demo-sound-toggle_icon"
              role="img"
              aria-label={demoVolume > 0 ? 'Выключить звук' : 'Включить звук'}
              style={{
                WebkitMaskImage: `url(${demoVolume > 0 ? '/img/icons/volume.png' : '/img/icons/volume_off.png'})`,
                maskImage: `url(${demoVolume > 0 ? '/img/icons/volume.png' : '/img/icons/volume_off.png'})`,
              }}
            />
          </button>
        )}

        <div className="menu-demo-match-end-banner_content">
          <div
            className="menu-demo-match-end-banner_header"
            onClick={handleToggleBannerCollapsed}
            title={isBannerCollapsed ? 'Развернуть' : 'Свернуть'}
          >
            <span className="menu-demo-match-end-banner_title">
              {hasMatchEnded ? 'Матч завершён!' : `Раунд ${round} из ${FINAL_ROUND}`}
            </span>
            {/* Клик обрабатывается на всей строке заголовка (см. onClick выше);
                у самой кнопки обработчика нет нарочно — клик по ней и так
                всплывает в заголовок, отдельный onClick вызвал бы двойной тоггл */}
            <button
              type="button"
              className="menu-demo-match-end-banner_collapse-button"
            >
              <img
                className={`menu-demo-match-end-banner_collapse-icon${isBannerCollapsed ? '' : ' menu-demo-match-end-banner_collapse-icon_expanded'}`}
                src="/img/icons/arrow down.png"
                alt={isBannerCollapsed ? 'Развернуть' : 'Свернуть'}
              />
            </button>
          </div>

          {hasMatchEnded && restartCountdownSec !== null && (
            <div className="menu-demo-match-end-banner_restart-countdown">
              {`Следующий матч через ${restartCountdownSec} сек`}
            </div>
          )}

          {!isBannerCollapsed && (
            <div className="menu-demo-match-end-players">
              {hasMatchEnded
                ? gameResults.map((result, index, sortedResults) => {
                  const fullPlayerState = getPlayerById(players, result.id) || {};
                  const faction = FACTION_CONFIGS.find((f) => f.id === fullPlayerState.factionId);
                  // Вместо имени бота показываем имя фракции; иконка бота скрыта
                  // нарочито — в демо-трансляции все участники боты, пометка не
                  // несет смысла. Награды/рейтинг отключены компактным режимом
                  const finalPlayer = {
                    ...result,
                    ...fullPlayerState,
                    name: getShowcasePlayerName(fullPlayerState, faction?.name, isReplayMode),
                    // В партии ботов пометка «бот» бессмысленна — там все боты;
                    // в записи реального матча она остается честной
                    isBot: isReplayMode ? Boolean(fullPlayerState.isBot) : false,
                  };
                  return (
                    <GameEndPlayerInfo
                      key={result.id}
                      player={finalPlayer}
                      index={index}
                      place={getGameResultsPlaces(sortedResults)[index]}
                      isRanked={false}
                      isCompactMode={true}
                      customClassName="match-details-player-info"
                    />
                  );
                })
                : [...players]
                  .map((player) => ({...player, finalWinPoints: getLiveFinalWinPoints(player)}))
                  .sort((a, b) => b.finalWinPoints - a.finalWinPoints)
                  .map((player, index) => {
                    const faction = FACTION_CONFIGS.find((f) => f.id === player.factionId);
                    // Та же подмена имени/скрытие бот-иконки, что и в
                    // финальном списке результатов выше — см. её комментарий
                    const livePlayer = {
                      ...player,
                      name: getShowcasePlayerName(player, faction?.name, isReplayMode),
                      isBot: isReplayMode ? Boolean(player.isBot) : false,
                    };
                    return (
                      <GameEndPlayerInfo
                        key={player.id}
                        player={livePlayer}
                        index={index}
                        isRanked={false}
                        isCompactMode={true}
                        coins={player.coins}
                        showPolicy
                        customClassName="match-details-player-info"
                        noPlaceIcon={true}
                      />
                    );
                  })}
            </div>
          )}
        </div>
      </div>
    </div>
    </ReplayControlsProvider>
  );
};

export default MenuDemoGame;
